import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  UserCheck,
  ClipboardList,
  CreditCard,
  Package,
  Settings,
  Calendar,
  Shield,
  UserCog,
  Repeat,
  FileText,
  Brain,
  Video,
  MessageSquare,
  FlaskConical,
  ShoppingCart,
  CheckCircle,
  Stethoscope,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';

interface CommandRoute {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords?: string[];
}

interface CommandGroup {
  heading: string;
  routes: CommandRoute[];
}

const COMMAND_GROUPS: CommandGroup[] = [
  {
    heading: 'Home',
    routes: [
      { label: 'Dashboard', path: '/', icon: LayoutDashboard, keywords: ['home', 'overview', 'metrics'] },
    ],
  },
  {
    heading: 'Clinical',
    routes: [
      { label: 'Patients', path: '/patients', icon: Users, keywords: ['patient', 'clients'] },
      { label: 'Practitioners', path: '/practitioners', icon: Stethoscope, keywords: ['doctor', 'provider'] },
      { label: 'Orders', path: '/prescriptions', icon: ClipboardList, keywords: ['rx', 'prescriptions'] },
      { label: 'IntelliHealth AI', path: '/intellihealth', icon: Brain, keywords: ['ai', 'intelligence', 'smart'] },
      { label: 'EMR Calendar', path: '/emr/calendar', icon: Calendar, keywords: ['emr', 'appointments', 'schedule'] },
      { label: 'Messaging', path: '/messaging', icon: MessageSquare, keywords: ['chat', 'messages'] },
      { label: 'Labs', path: '/labs', icon: FlaskConical, keywords: ['lab', 'tests', 'results'] },
    ],
  },
  {
    heading: 'Storefront',
    routes: [
      { label: 'Storefront', path: '/storefront', icon: ShoppingBag, keywords: ['store', 'shop', 'products'] },
      { label: 'Cart', path: '/storefront/cart', icon: ShoppingCart, keywords: ['cart', 'checkout'] },
      { label: 'Products', path: '/products', icon: Package, keywords: ['medications', 'catalog'] },
      { label: 'Subscriptions', path: '/subscriptions', icon: Repeat, keywords: ['recurring', 'plans'] },
    ],
  },
  {
    heading: 'Operations',
    routes: [
      { label: 'Payments', path: '/payments', icon: CreditCard, keywords: ['billing', 'transactions', 'finance'] },
      { label: 'Intakes', path: '/intakes', icon: FileText, keywords: ['forms', 'questionnaire'] },
    ],
  },
  {
    heading: 'Admin',
    routes: [
      { label: 'Settings', path: '/settings', icon: Settings, keywords: ['config', 'preferences'] },
      { label: 'Roles', path: '/roles', icon: Shield, keywords: ['permissions', 'access'] },
      { label: 'Users', path: '/users', icon: UserCog, keywords: ['team', 'members'] },
      { label: 'Admins', path: '/admins', icon: UserCheck, keywords: ['administrators'] },
    ],
  },
];

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette({ open: controlledOpen, onOpenChange }: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  const { clinic } = useClinic();
  const isGuided = clinic?.clinic_archetype === 'guided';

  const commandGroups = useMemo(() =>
    COMMAND_GROUPS.map(group => ({
      ...group,
      routes: group.routes.map(route =>
        route.path === '/intakes' && isGuided ? { ...route, label: 'Enrollments' } : route
      ),
    })), [isGuided]);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  const handleSelect = useCallback(
    (path: string) => {
      setIsOpen(false);
      navigate(path);
    },
    [navigate, setIsOpen]
  );

  return (
    <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
      <CommandInput placeholder="Search pages, features, settings..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {commandGroups.map((group, groupIdx) => (
          <React.Fragment key={group.heading}>
            {groupIdx > 0 && <CommandSeparator />}
            <CommandGroup heading={group.heading}>
              {group.routes.map((route) => (
                <CommandItem
                  key={route.path}
                  value={`${route.label} ${route.keywords?.join(' ') || ''}`}
                  onSelect={() => handleSelect(route.path)}
                  className="cursor-pointer"
                >
                  <route.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{route.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
